import Cookies from 'js-cookie';
import { jwtDecode, JwtPayload } from 'jwt-decode';

interface UserPayload {
  email: string;
  name: string;
  user_id: string;
  given_name: string;
  family_name: string;
  sub: string;
  is_impersonating: string;
}
type Jwt = JwtPayload & UserPayload;

const cookieNameCVIdToken = 'CVIdToken';
const cookieNameCVAccessToken = 'CVAccessToken';

export interface Token {
  /** Cookie token that was used */
  token: typeof cookieNameCVIdToken | typeof cookieNameCVAccessToken | 'MISSING';
  /** Decoded jwt */
  jwt: Jwt;
  /** Is the token valid */
  isValidToken: boolean;
  /** Is the user logged in */
  isLoggedIn: boolean;
  /** user payload from jwt */
  user: UserPayload;

  cvIdToken?: string;
  cvAccessToken?: string;
}

interface AccessToken {
  access_token?: string | null;
}

const decodeCookie = (accessToken: string) => {
  try {
    const jwt = jwtDecode<Jwt>(accessToken);
    return jwt;
  } catch (error) {
    return {} as Jwt;
  }
};

export const getCvAccessToken = () => {
  const CVAccessToken = Cookies.get(cookieNameCVAccessToken) ?? '';
  if (CVAccessToken) {
    let token = '';
    try {
      const tokenObj = JSON.parse(CVAccessToken) as AccessToken;
      token = tokenObj.access_token ?? CVAccessToken;
    } catch (error) {
      token = CVAccessToken;
    }

    return token;
  }

  return '';
};

export const shouldRefresh = () => {
  const CVIdToken = Cookies.get(cookieNameCVIdToken) ?? '';
  const CVAccessToken = getCvAccessToken();
  return !!CVIdToken && !CVAccessToken;
};

const accessToken = (): Token => {
  const CVIdToken = Cookies.get(cookieNameCVIdToken) ?? '';
  const CVAccessToken = getCvAccessToken();
  const hasIdTokenAndAccessToken = !!CVIdToken && !!CVAccessToken;
  const hasAccessTokenAndNoIdToken = !CVIdToken && !!CVAccessToken;

  if (hasIdTokenAndAccessToken || hasAccessTokenAndNoIdToken) {
    const jwt = hasIdTokenAndAccessToken ? decodeCookie(CVIdToken) : decodeCookie(CVAccessToken);
    const token = hasIdTokenAndAccessToken ? cookieNameCVIdToken : cookieNameCVAccessToken;

    return {
      token,
      jwt,
      isValidToken: Object.keys(jwt).length > 0,
      isLoggedIn: !!jwt.user_id,
      user: {
        email: jwt.email,
        name: jwt.name,
        user_id: jwt.user_id,
        given_name: jwt.given_name,
        family_name: jwt.family_name,
        sub: jwt.sub,
        is_impersonating: jwt.is_impersonating
      },
      cvIdToken: CVIdToken,
      cvAccessToken: CVAccessToken
    };
  }

  return {
    token: 'MISSING',
    jwt: {} as Jwt,
    isValidToken: false,
    isLoggedIn: false,
    user: {} as UserPayload,
    cvIdToken: CVIdToken,
    cvAccessToken: CVAccessToken
  };
};

export default accessToken;
export { hasEngSupportRole } from './eng-support-role';
